import './IdleVsHp.scss';

import { IonButton, IonCard, IonCardContent, IonCardHeader, IonIcon, IonRow } from '@ionic/react';
import * as venn from '@upsetjs/venn.js';
import * as d3 from 'd3';
import { informationCircle } from 'ionicons/icons';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { JobMessages } from 'src/intl/JobMessages';
import { formatDaysHoursMinutes } from 'src/utils/helpers/chartHelpers';
import { JobLogV2Data } from 'src/utils/logs/types';
import { DqText } from '../dq-text';
import VennDiagramModal from '../job-details/information/information-modals/VennDiagramModal';
import { GraphTooltipController } from '../job-details/util/GraphTooltipController';
import { IdleVsHpLegendRow } from './idleVsHpLegendRow';
import { Range, RangeSet } from './utils';

interface IdleVsHpGraphSetOverlap extends venn.IStyledSetOverlap {
  tooltip?: string;
  label?: string;
}

class IdleVsHpGraphController {
  private readonly root: d3.Selection<HTMLElement, IdleVsHpGraphSetOverlap, null, never>;
  private readonly chart: venn.IVennDiagram;
  private readonly tooltip: GraphTooltipController;

  constructor(root: HTMLElement) {
    this.root = d3.select(root);
    this.chart = venn.VennDiagram();
    this.chart.width(300).height(200).useViewBox();
    this.root.datum([] as any).call(this.chart);
    this.tooltip = new GraphTooltipController(this.root.select<SVGSVGElement>('svg').node()!);
  }

  public readonly setData = (data: readonly IdleVsHpGraphSetOverlap[]) => {
    this.root.datum(data).call(this.chart);
    this.setupMouseEvents();
  };

  private readonly setupMouseEvents = () => {
    const self = this;
    this.root
      .selectAll<SVGGElement, IdleVsHpGraphSetOverlap>('g.venn-area')
      .on('mouseover', function (this, ev, data) {
        const [x, y] = d3.pointer(ev, self.root.select<SVGSVGElement>('svg').node()!);
        const lines = (data.tooltip ?? data.label)?.split('\n');
        if (!lines) return;
        self.tooltip.setVisible(true);
        self.tooltip.setText(lines, x, y);
      })
      .on('mousemove', (ev, data) => {
        const lines = (data.tooltip ?? data.label)?.split('\n');
        if (!lines) return;
        const [x, y] = d3.pointer(ev, this.root.select<SVGSVGElement>('svg').node()!);
        this.tooltip.setText(lines, x, y);
      })
      .on('mouseout', () => {
        this.tooltip.setVisible(false);
      });
  };
}

export interface IdleVsHpInfo {
  valve2: JobLogV2Data['valveStatus'][number];
  valve3: JobLogV2Data['valveStatus'][number];
  valve5: JobLogV2Data['valveStatus'][number];
  valve6: JobLogV2Data['valveStatus'][number];
  valve16: JobLogV2Data['valveStatus'][number];
  showTimeDetails: boolean;
  controllerOffRanges: RangeSet;
  /** Time range based on calendar time if FW v >= 2.3.18, otherwise based on job ms elapsed and matches timeRange */
  logFileTimeRangeMin: number;
  logFileTimeRangeMax: number;
}

const IdleVsHp: React.FC<IdleVsHpInfo> = ({
  valve2,
  valve3,
  valve5,
  valve6,
  valve16,
  showTimeDetails,
  controllerOffRanges,
  logFileTimeRangeMin,
  logFileTimeRangeMax,
}) => {
  const { formatMessage } = useIntl();
  const [helpDialogOpen, setHelpDialogOpen] = useState<boolean>(false);
  const chartRef = useRef<HTMLDivElement>(null);
  const ctrlRef = useRef<IdleVsHpGraphController | null>(null);

  const highPressureEvents = useMemo(() => {
    return [valve3.valveA, valve3.valveB, valve16.valveA, valve16.valveB].flat();
  }, [valve16.valveA, valve16.valveB, valve3.valveA, valve3.valveB]);

  const highPressureRange = useMemo<RangeSet>(() => {
    const set = new RangeSet(logFileTimeRangeMin, Math.max(logFileTimeRangeMin + 1, logFileTimeRangeMax));
    try {
      set.addRange(...highPressureEvents.map(Range.fromValve));
    } catch (err) {
      console.warn('Error parsing HP event range for idle vs hp diagram.', err);
      return new RangeSet(logFileTimeRangeMin, Math.max(logFileTimeRangeMin + 1, logFileTimeRangeMax));
    }
    return set;
  }, [highPressureEvents, logFileTimeRangeMax, logFileTimeRangeMin]);

  const otherPressureEvents = useMemo(() => {
    return [valve2.valveA, valve2.valveB, valve5.valveA, valve5.valveB, valve6.valveA, valve6.valveB].flat();
  }, [valve2.valveA, valve2.valveB, valve5.valveA, valve5.valveB, valve6.valveA, valve6.valveB]);

  /** For Controller FW >= 2.3.18: Get all times controller was off during the range so we can exclude it from the idle time */
  const filteredControllerOffRanges = useMemo(() => {
    const set = new RangeSet(logFileTimeRangeMin, Math.max(logFileTimeRangeMin + 1, logFileTimeRangeMax));
    try {
      set.addRange(...controllerOffRanges.getRangesBeforePointInTime(logFileTimeRangeMax));
    } catch (err) {
      console.warn('Error parsing off range for system pie chart.', err);
    }
    return set;
  }, [controllerOffRanges, logFileTimeRangeMin, logFileTimeRangeMax]);

  const idleRange = useMemo<RangeSet>(() => {
    const set = new RangeSet(logFileTimeRangeMin, Math.max(logFileTimeRangeMin + 1, logFileTimeRangeMax));
    try {
      set.addRange(...otherPressureEvents.map(Range.fromValve));
      set.addRange(...filteredControllerOffRanges.getRangesBeforePointInTime(logFileTimeRangeMax));
      return set.invert();
    } catch (err) {
      console.warn('Error parsing other pressure event range for idle vs hp diagram.');
      return new RangeSet(logFileTimeRangeMin, Math.max(logFileTimeRangeMin + 1, logFileTimeRangeMax));
    }
  }, [otherPressureEvents, logFileTimeRangeMax, logFileTimeRangeMin, filteredControllerOffRanges]);

  const chartData = React.useMemo<IdleVsHpGraphSetOverlap[]>(() => {
    // Overlap calculation can throw, try catch to handle failure to parse gracefully.
    let overlapSize = 0;
    try {
      const overlap = highPressureRange.intersect(idleRange);
      overlapSize = overlap.sumIncluded();
    } catch (e) {
      console.warn('Error computing overlap for idle vs hp diagram', e);
    }

    // Sum included does not contain a throw
    const hpSize = highPressureRange.sumIncluded();
    const idleSize = idleRange.sumIncluded();

    return [
      {
        sets: ['H'],
        size: !!hpSize ? hpSize : 0,
        label: formatMessage(JobMessages.jobGraphIdleVsHpLabelHp),
        className: 'idle-hp-graph-area-hp',
      },
      {
        sets: ['I'],
        size: !!idleSize ? idleSize : 0,
        label: formatMessage(JobMessages.jobGraphIdleVsHpLabelIdle),
        className: 'idle-hp-graph-area-idle',
      },
      {
        sets: ['I', 'H'],
        size: !!overlapSize ? overlapSize : 0,
        tooltip: `${formatMessage(JobMessages.jobGraphIdleVsHpLabelOverlap)}\n${formatDaysHoursMinutes(overlapSize)}`,
        className: 'idle-hp-graph-area-overlap',
      },
    ];
  }, [formatMessage, highPressureRange, idleRange]);

  // An effect that initializes the graph
  useEffect(() => {
    const root = chartRef.current;
    if (root) {
      ctrlRef.current = new IdleVsHpGraphController(root);
    } else {
      console.error('IdleVsHp graph failed to initialize!');
    }
  }, []);

  // An effect that updates the graph whenever the data changes.
  useEffect(() => {
    ctrlRef.current?.setData(chartData);
  }, [chartData]);

  return (
    <IonCard className="idle-hp-card job-graph-card">
      <IonCardHeader>
        <IonRow className="ion-justify-content-between ion-align-items-center">
          <DqText variant="h3">{formatMessage(JobMessages.sentinelIdleVsPumpAtPressure)}</DqText>
          <IonButton fill="clear" color="primary" onClick={() => setHelpDialogOpen(true)}>
            <IonIcon slot="icon-only" icon={informationCircle} />
          </IonButton>
        </IonRow>
      </IonCardHeader>
      <IonCardContent className="idle-hp-graph-container">
        <div className="idle-hp-graph" ref={chartRef} />
        <div className="idle-hp-graph-legend">
          <IdleVsHpLegendRow
            legendHeading={formatMessage(JobMessages.highPressureHeading)}
            showTimeDetails={showTimeDetails}
            dotColorClass="ion-color-data-blue"
            chartDataSize={chartData[0].size}
          />
          <IdleVsHpLegendRow
            legendHeading={formatMessage(JobMessages.idleOff)}
            showTimeDetails={showTimeDetails}
            dotColorClass="ion-color-data-yellow"
            chartDataSize={chartData[1].size}
          />
          <IdleVsHpLegendRow
            legendHeading={formatMessage(JobMessages.overlap)}
            showTimeDetails={showTimeDetails}
            dotColorClass="ion-color-data-green"
            chartDataSize={chartData[2].size}
          />
        </div>
      </IonCardContent>
      <VennDiagramModal isOpen={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} />
    </IonCard>
  );
};

export default IdleVsHp;
